import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, Briefcase, ArrowRight } from "lucide-react";
import { api } from "../lib/api";

export default function JobSearch({ size = "lg", className = "" }) {
  const [q, setQ] = useState("");
  const [where, setWhere] = useState("");
  const [role, setRole] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!q.trim()) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      const r = await api.get(`/vacancies?q=${encodeURIComponent(q)}&limit=5`);
      setSuggestions(r.data); setOpen(true);
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const submit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (where.trim()) params.set("region", where.trim());
    if (role) params.set("role", role);
    nav(`/vacancies?${params.toString()}`);
  };

  return (
    <form onSubmit={submit} className={`relative ${className}`} data-testid="job-search">
      <div className={`flex flex-col md:flex-row md:items-center gap-2 md:gap-0 paper-card !rounded-full px-2.5 py-2 md:py-1.5 ${size === "lg" ? "" : ""}`}>
        <div className="flex items-center gap-2 flex-1 min-w-0 px-3">
          <Search className="w-4 h-4 text-sky shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => suggestions.length && setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            placeholder="Job title or keyword (e.g. Train Driver)"
            className="bg-transparent outline-none w-full py-3 text-[14px] placeholder:text-slate"
            data-testid="search-q"
          />
        </div>
        <div className="hidden md:block w-px h-7 bg-line" />
        <div className="flex items-center gap-2 flex-1 min-w-0 px-3">
          <MapPin className="w-4 h-4 text-sky shrink-0" />
          <input
            value={where}
            onChange={(e) => setWhere(e.target.value)}
            placeholder="Region (e.g. North West)"
            className="bg-transparent outline-none w-full py-3 text-[14px] placeholder:text-slate"
            data-testid="search-where"
          />
        </div>
        <div className="hidden md:block w-px h-7 bg-line" />
        <div className="flex items-center gap-2 flex-1 min-w-0 px-3">
          <Briefcase className="w-4 h-4 text-sky shrink-0" />
          <select
            value={role} onChange={(e) => setRole(e.target.value)}
            className="bg-transparent outline-none w-full py-3 text-[14px] text-ice cursor-pointer"
            data-testid="search-role"
          >
            <option value="" className="bg-surf text-ice">Any role</option>
            <option value="train-driver" className="bg-surf text-ice">Train Driver</option>
            <option value="conductor" className="bg-surf text-ice">Conductor</option>
            <option value="depot-driver" className="bg-surf text-ice">Depot Driver</option>
            <option value="fitter" className="bg-surf text-ice">Fitter / Maintenance</option>
            <option value="engineering" className="bg-surf text-ice">Engineering</option>
            <option value="signalling" className="bg-surf text-ice">Signalling</option>
            <option value="customer-service" className="bg-surf text-ice">Customer Service</option>
            <option value="graduate" className="bg-surf text-ice">Graduate</option>
            <option value="apprentice" className="bg-surf text-ice">Apprenticeship</option>
          </select>
        </div>
        <button type="submit" className="pill pill-primary md:ml-2" data-testid="search-submit">
          Search <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {open && suggestions.length > 0 && (
        <div className="absolute left-0 right-0 mt-2 paper-card !rounded-2xl overflow-hidden shadow-lift z-50">
          {suggestions.map((s) => (
            <button
              key={s.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); nav(`/vacancies/${s.id}`); }}
              className="w-full text-left px-5 py-3 hover:bg-raise transition-colors border-b border-line last:border-0"
              data-testid={`search-suggestion-${s.id}`}
            >
              <div className="text-ice font-semibold text-[14px]">{s.title}</div>
              <div className="text-slate text-[12px] mt-0.5">{s.employer_name} · {s.location}</div>
            </button>
          ))}
        </div>
      )}
    </form>
  );
}
